import type { Variants } from 'framer-motion';

/**
 * Shared motion vocabulary.
 *
 * One easing curve and three durations across the whole product — the thing
 * that separates "animated" from "designed" is consistency, not variety.
 * Components that animate always pair these with `useReducedMotion()`.
 */
export const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;
export const EASE_IN_OUT = [0.76, 0, 0.24, 1] as const;

export const DURATION = {
  fast: 0.25,
  base: 0.6,
  slow: 0.9,
} as const;

/** Standard reveal: rise and fade as the element enters the viewport. */
export const revealUp: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: DURATION.base, ease: EASE_OUT_EXPO },
  },
};

export const revealFade: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.slow, ease: EASE_OUT_EXPO } },
};

export const revealScale: Variants = {
  hidden: { opacity: 0, scale: 0.94 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: DURATION.base, ease: EASE_OUT_EXPO },
  },
};

/** Parent that staggers its children's reveal. */
export const stagger = (delayChildren = 0, staggerChildren = 0.08): Variants => ({
  hidden: {},
  visible: { transition: { delayChildren, staggerChildren } },
});

/** Word-by-word headline reveal. Split the string, map over `wordReveal`. */
export const wordReveal: Variants = {
  hidden: { opacity: 0, y: '0.5em', rotateX: -35 },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: { duration: 0.75, ease: EASE_OUT_EXPO },
  },
};

/** Viewport config used by every scroll-triggered section. */
export const viewportOnce = { once: true, amount: 0.25 } as const;
export const viewportEarly = { once: true, amount: 0.1 } as const;

export const hoverLift = {
  whileHover: { y: -4, transition: { duration: DURATION.fast, ease: EASE_OUT_EXPO } },
  whileTap: { scale: 0.985 },
} as const;

export function splitWords(text: string): string[] {
  return text.split(/(\s+)/).filter((chunk) => chunk.trim().length > 0);
}
